import React, { useState, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { useDataManagement } from '../ferramentas/RequisitarAPI.ts';

// Importando os componentes modularizados
import { ErrorMessage, NoDataMessage } from './components/TableFeedback';
import SortIndicator from './components/SortIndicator';
import UserSelectionModal from './components/UserSelectionModal';

const FaturamentoSection = ({ styles, formatCurrency, initialDateRange }) => {
    // Hook para interagir com a API
    const { reports, users, error, load } = useDataManagement();
    
    // Estado principal de filtros
    const [filters, setFilters] = useState({
        start_date: initialDateRange.inicio,
        final_date: initialDateRange.fim,
        users: [], // Array de IDs
        page: 1,
        limit: 10,
        sort: { by: 'date', order: 'desc' },
    });

    // Estados de controle de UI e dados
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [displayedRows, setDisplayedRows] = useState([]);
    const [funcionarios, setFuncionarios] = useState([]);

    // Refs para o gráfico
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    // --- EFEITOS (LÓGICA) ---

    // 1. Busca a lista de funcionários para o modal (apenas uma vez)
    useEffect(() => {
        const fetchFuncionarios = async () => {
            const response = await users.list();
            if (response.ok && response.data) {
                const userList = Object.values(response.data).map(user => ({ id: user.id, nome: user.fullname }));
                setFuncionarios(userList);
            }
        };
        fetchFuncionarios();
    }, [users]);

    // 2. Busca os dados do relatório sempre que os filtros mudam
    useEffect(() => {
        const fetchReport = async () => {
            const apiFilters = {
                start_date: filters.start_date || undefined,
                final_date: filters.final_date || undefined,
                users: filters.users.length > 0 ? filters.users : undefined,
                page: filters.page,
                limit: parseInt(filters.limit, 10),
                // sort_by: filters.sort.by,
                // sort_order: filters.sort.order,
            };
            const response = await reports.revenue(apiFilters);
            if (!response.ok) {
                console.error('Erro ao buscar relatório de faturamento:', response.message);
                return;
            }
            setReportData(response.ok ? response.data : null);
        };
        fetchReport();
    }, [filters, reports]);

    // 3. Aplica a ordenação local nos dados da página atual
    useEffect(() => {
        if (!reportData || !reportData.data) {
            setDisplayedRows([]);
            return;
        }
        const sortedPageData = [...reportData.data].sort((a, b) => {
            const valA = a[filters.sort.by];
            const valB = b[filters.sort.by];
            if (valA < valB) return filters.sort.order === 'asc' ? -1 : 1;
            if (valA > valB) return filters.sort.order === 'asc' ? 1 : -1;
            return 0;
        });
        setDisplayedRows(sortedPageData);
    }, [reportData, filters.sort]);

    // 4. Renderiza e atualiza o gráfico
    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }
        if (chartRef.current && reportData?.data?.length > 0) {
            const ctx = chartRef.current.getContext('2d');
            const chartData = [...reportData.data].sort((a, b) => new Date(a.date) - new Date(b.date));
            const chartLabels = chartData.map(item => new Date(item.date + 'T00:00:00').toLocaleDateString('pt-BR'));
            const chartValues = chartData.map(item => item.total);

            chartInstanceRef.current = new Chart(ctx, {
                type: 'line',
                data: { labels: chartLabels, datasets: [{ label: 'Faturamento Total por Dia', data: chartValues, borderColor: '#8b5cf6', backgroundColor: 'rgba(139, 92, 246, 0.2)', tension: 0.1, fill: true }] },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    scales: { y: { beginAtZero: true, ticks: { color: '#9ca3af' }, grid: { color: '#374151' } }, x: { ticks: { color: '#9ca3af' }, grid: { color: '#374151' } } },
                    plugins: { legend: { labels: { color: '#d1d5db' } } }
                }
            });
        }
        return () => { if (chartInstanceRef.current) chartInstanceRef.current.destroy(); };
    }, [reportData]);


    // --- MANIPULADORES DE EVENTOS ---

    const handleFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value, page: 1 }));
    const handleUserSelectionConfirm = (newUsers) => setFilters(prev => ({ ...prev, users: newUsers, page: 1 }));
    const handleSort = (columnName) => setFilters(prev => ({...prev, sort: {by: columnName, order: prev.sort.by === columnName && prev.sort.order === 'desc' ? 'asc' : 'desc' }, page: 1 }));
    const handleNextPage = () => setFilters(prev => ({ ...prev, page: prev.page + 1 }));
    const handlePreviousPage = () => setFilters(prev => ({ ...prev, page: prev.page - 1 }));


    // --- FUNÇÕES AUXILIARES DE RENDERIZAÇÃO ---

    const totalPages = reportData ? Math.ceil(reportData.total / filters.limit) : 1;
    const getUserButtonText = () => {
        if (filters.users.length === 0) return 'Todos os Funcionários';
        if (filters.users.length === 1) return '1 funcionário selecionado';
        return `${filters.users.length} funcionários selecionados`;
    };

    // --- RENDERIZAÇÃO DO COMPONENTE (JSX) ---

    return (
        <>
            <UserSelectionModal
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                allUsers={funcionarios}
                selectedUsers={filters.users}
                onConfirm={handleUserSelectionConfirm}
                styles={styles}
            />

            <div id="faturamento" className={`${styles.tabContent} ${styles.activeContent} ${styles.card}`}>
                <h2 className={styles.cardTitle}>Relatório de Faturamento</h2>
                
                <div className={`${styles.filterSection} ${styles.colsMd2} ${styles.colsLg4}`}>
                    <div>
                        <label>Data Início</label>
                        <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} />
                    </div>
                    <div>
                        <label>Data Fim</label>
                        <input type="date" name="final_date" value={filters.final_date} onChange={handleFilterChange} />
                    </div>
                    <div>
                        <label>Funcionário(s)</label>
                        <button type="button" className={styles.modalTriggerButton} onClick={() => setIsUserModalOpen(true)}>
                            {getUserButtonText()}
                        </button>
                    </div>
                    <div>
                        <label>Itens por Página</label>
                        <select name="limit" value={filters.limit} onChange={handleFilterChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="250">250</option>
                        </select>
                    </div>
                </div>

                <div className={styles.tableResponsiveWrapper}>
                    <table id="faturamento-table" className={`${styles.table} ${styles.tableSortable}`}>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('date')}>Data <SortIndicator column="date" sortConfig={filters.sort} /></th>
                                <th onClick={() => handleSort('cards')}>Compromissos <SortIndicator column="cards" sortConfig={filters.sort} /></th>
                                <th onClick={() => handleSort('total')}>Faturamento Total <SortIndicator column="total" sortConfig={filters.sort} /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {load.isLoading ? 
                                <div id="loading-overlay">
                                    <div>
                                    <div id="loading-spinner"></div>
                                    <p>{load.reason}</p>
                                    </div>
                                </div>
                             : error ? <ErrorMessage error={error} styles={styles} />
                             : displayedRows.length > 0 ? (
                                displayedRows.map((row) => (
                                    <tr key={row.date}>
                                        <td>{new Date(row.date + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                                        <td>{row.cards}</td>
                                        <td>{formatCurrency(row.total)}</td>
                                    </tr>
                                ))
                            ) : <NoDataMessage styles={styles} />}
                        </tbody>
                        {!load.isLoading && !error && reportData?.data?.length > 0 && (
                            <tfoot>
                                <tr>
                                    <td>Total Geral ({reportData.total} registros no total)</td>
                                    <td>{reportData.total_cards}</td>
                                    <td colSpan="1">
                                        {formatCurrency(reportData.total_financial)} 
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {!load.isLoading && !error && reportData && reportData.total > filters.limit && (
                    <div className={styles.paginationControls}>
                        <button onClick={handlePreviousPage} disabled={filters.page <= 1}>Anterior</button>
                        <span>Página {filters.page} de {totalPages}</span>
                        <button onClick={handleNextPage} disabled={filters.page >= totalPages}>Próxima</button>
                    </div>
                )}
                
                <div className={styles.chartContainer}>
                    <canvas ref={chartRef} id="faturamentoChart"></canvas>
                </div>
            </div>
        </>
    );
};

export default FaturamentoSection;